import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { message } from 'antd'
import { IPhoneNumber, IPhoneNumberGroup } from '../../interface'
import { PhoneNumbers } from '../PhoneNumbers'
import { PhoneNumberSearchInput } from '../PhoneNumberSearchInput'
import { filterPhoneNumberGroups, formatPhoneNumber } from '../../utils'
import { CloseIcon } from '@forwardflow/icons'
import { usePhoneNumbers } from '../../hooks'

interface AllocateUnusedNumbersViewProps {
   groupedInactivePhoneNumbers: IPhoneNumberGroup[]
   onNumbersSelected?: (numbers: IPhoneNumber[]) => void
   setSelectedTab?: (tab: string) => void
   campaignId: string
   onConfirmAllocate?: (numbers: IPhoneNumber[]) => Promise<void>
}

export const AllocateUnusedNumbersView = ({
   groupedInactivePhoneNumbers,
   onNumbersSelected,
   setSelectedTab,
   campaignId,
   onConfirmAllocate,
}: AllocateUnusedNumbersViewProps) => {
   const { assignPhoneNumber } = usePhoneNumbers()
   const [searchQuery, setSearchQuery] = useState('')
   const [selectedNumbers, setSelectedNumbers] = useState<IPhoneNumber[]>([])
   const [isAssigning, setIsAssigning] = useState(false)

   const filteredInactiveGroups = useMemo(
      () => filterPhoneNumberGroups(groupedInactivePhoneNumbers, searchQuery),
      [groupedInactivePhoneNumbers, searchQuery]
   )

   const handleSelectNumber = (number: IPhoneNumber) => {
      const isSelected = selectedNumbers.some(
         (selectedNumber) =>
            selectedNumber.phoneNumber.phoneNumber ===
            number.phoneNumber.phoneNumber
      )

      const updatedNumbers = isSelected
         ? selectedNumbers.filter(
              (selectedNumber) =>
                 selectedNumber.phoneNumber.phoneNumber !==
                 number.phoneNumber.phoneNumber
           )
         : [...selectedNumbers, number]

      setSelectedNumbers(updatedNumbers)
      onNumbersSelected?.(updatedNumbers)
   }

   const handleConfirm = async () => {
      setIsAssigning(true)
      try {
         if (onConfirmAllocate) {
            await onConfirmAllocate(selectedNumbers)
         } else {
            await assignPhoneNumber(
               selectedNumbers.map((number) => number.phoneNumber.phoneNumber),
               campaignId
            )
         }
         setSelectedTab?.('list-of-numbers')
         setSelectedNumbers([])
      } catch (error) {
         console.error('Error assigning numbers to campaign:', error)
         message.error('Failed to allocate phone numbers. Please try again.')
      } finally {
         setIsAssigning(false)
      }
   }
   

   return (
      <div className="flex flex-col h-full">
         {/* Fixed Header Section */}
         <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-shrink-0"
         >
            <div className="p-4 bg-white/[0.05] mr-3 rounded-lg space-y-5">
               <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <p className="text-white text-sm font-medium">
                     Select Numbers to add to Campaign
                  </p>
                  <div className="flex items-center gap-x-1.5">
                     <motion.button
                        key="assign-button"
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                           setSelectedTab?.('list-of-numbers')
                           setSelectedNumbers([])
                        }}
                        disabled={isAssigning}
                        className="bg-black/20 rounded-md px-4 py-1.5 text-white text-xs font-medium hover:bg-black/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        Cancel
                     </motion.button>
                     <motion.button
                        disabled={selectedNumbers.length === 0 || isAssigning}
                        key="confirm-button"
                        whileTap={{ scale: 0.95 }}
                        onClick={handleConfirm}
                        className="bg-[#9061DA66] rounded-md px-4 py-1.5 text-white text-xs font-medium hover:bg-black/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-1.5 justify-center"
                     >
                        {isAssigning && (
                           <motion.div
                              animate={{ rotate: 360 }}
                              transition={{
                                 duration: 1,
                                 repeat: Infinity,
                                 ease: 'linear',
                              }}
                              className="w-3 h-3 border-2 border-white border-t-transparent rounded-full"
                           />
                        )}
                        {isAssigning ? 'Allocating...' : 'Confirm'}
                     </motion.button>
                  </div>
               </div>
               {/* Selected Numbers Section */}
               {selectedNumbers.length > 0 ? (
                  <div className="pr-3">
                     <p className="text-white text-sm font-medium mb-3">
                        Selected Numbers ({selectedNumbers.length})
                     </p>
                     <div className="flex flex-wrap gap-2">
                        {selectedNumbers.map((number) => (
                           <div
                              key={number.phoneNumber.phoneNumber}
                              className="flex items-center gap-x-2 bg-[#D9D9D9]/10 rounded-lg px-4 py-2 pr-2"
                           >
                              <span className="text-white text-sm">
                                 {formatPhoneNumber(
                                    number.phoneNumber.phoneNumber
                                 )}
                              </span>
                              <button
                                 onClick={() => handleSelectNumber(number)}
                                 className="text-white/60 hover:text-white"
                              >
                                 <CloseIcon className="w-3 h-3" />
                              </button>
                           </div>
                        ))}
                     </div>
                  </div>
               ) : (
                  <div className="flex items-center justify-center py-6">
                     <p className="text-white/40 text-sm">
                        No numbers selected
                     </p>
                  </div>
               )}
            </div>
            <div className="mt-4">
               <PhoneNumberSearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search Phone numbers"
               />
            </div>
            <div className="flex items-center justify-between pr-3 mt-6">
               <p className="text-white text-sm font-medium">
                  Available numbers
               </p>
            </div>
         </motion.div>

         {/* Scrollable List Section */}
         <div className="flex-1 min-h-0 overflow-y-auto mt-2">
            {filteredInactiveGroups.length > 0 ? (
               <PhoneNumbers
                  numberGroups={filteredInactiveGroups}
                  showMessageCount={false}
                  selectMode={true}
                  onSelect={handleSelectNumber}
                  selectedNumbers={selectedNumbers}
               />
            ) : (
               <div className="flex items-center justify-center py-12">
                  <p className="text-white/40 text-sm">
                     No phone numbers found
                     {searchQuery && ` for "${searchQuery}"`}
                  </p>
               </div>
            )}
         </div>
      </div>
   )
}
